import { useMutation, useQuery } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import Loading from "@components/common/loading/Loading";
import NoteCreatedAt from "@components/notebook/note/NoteCreatedAt";
import NoteStatus from "@components/notebook/note/NoteStatus";
import { MODIFY_NOTE } from "@components/notes/edit/api/mutations";
import { GET_NOTE } from "@components/notes/EditNote";
import { MaximizedPipControls } from "@components/pip/PipSizeControls";
import {
  faCheckCircle,
  faCopy,
} from "@fortawesome/free-regular-svg-icons";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faPencil,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MARK_NOTES_EXPORTED from "@graphql/mutations/MarkNotesExported";
import {
  usePimsExportFormat,
  useLocalStorage,
  useWatchUserWsEvent,
} from "@hooks";
import { useDisplayName } from "@hooks/use-display-name";
import { Tooltip } from "@mui/material";
import { stripNbsp } from "@src/utils";
import { getClipboardPayload } from "@src/utils/clipboard";
import { writeToClipboardWithPimsFormat } from "@src/utils/clipboard";
import { trackOperation } from "@src/utils/operationTracking";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

async function copyToClipboard(textContent, format) {
  toast.dismiss(); // Dismiss all existing toasts to avoid clutter if user spams

  if (!textContent) {
    alert("info", `Nothing to copy`);
    return;
  }

  try {
    await writeToClipboardWithPimsFormat(textContent, format);
    alert("success", `Copied to clipboard!`);
    mixpanel.track("Copied Note", {
      copyType: "pip_section",
      pimsExportFormat: format,
    });
  } catch {
    alert("error", "Failed to copy to clipboard.");
  }
}

function DraggableButton({
  label,
  children,
  colorClasses,
  noteUuid,
  mxSectionLabel,
  onCopy,
  format,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const content = stripNbsp(children || "");

  function onDragStart(evt) {
    if (!content) {
      alert("info", `Nothing to copy`);
      return;
    }

    const { plain, html } = getClipboardPayload(content, format);

    evt.dataTransfer.setData("text/plain", plain);
    evt.dataTransfer.setData(
      "text/html",
      html || plain.replaceAll("\n", "<br />"),
    );

    onCopy?.();
  }

  function onClickCopy() {
    copyToClipboard(content, format);
    onCopy?.();
  }

  colorClasses =
    colorClasses ||
    "bg-brand hover:bg-indigo-600 disabled:bg-disabled";

  return (
    <div className="my-2 relative text-white">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        data-mx-label={`pip-section-expand-${mxSectionLabel}`}
        className="absolute top-0 bottom-0 right-0 rounded-full bg-white/20 w-11 h-11 flex items-center justify-center"
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
        />
      </button>

      <button
        draggable
        onClick={onClickCopy}
        onDragStart={onDragStart}
        className={`rounded-full flex-row text-center h-11 w-full ${colorClasses}`}
        data-mx-label={`pip-section-copy-${mxSectionLabel}`}
      >
        {label}
        &nbsp;
        <FontAwesomeIcon icon={faCopy} />
      </button>

      {isExpanded && (
        <div className="mt-2 bg-secondary text-primary p-3 rounded-xl text-xs whitespace-pre-line whitespace-pre-wrap">
          {/* Floating section with buttons */}
          <div className="float-right flex gap-2 cursor-pointer m-2">
            <ExpandedSectionButton
              title="Copy to clipboard"
              onClick={onClickCopy}
              data-mx-label={`pip-section-expanded-copy-${mxSectionLabel}`}
            >
              <FontAwesomeIcon icon={faCopy} size="2x" />
            </ExpandedSectionButton>
            <ExpandedSectionButton
              title="Edit in Scribenote"
              href={`/edit_note/${noteUuid}`}
              rel="noreferrer"
              target="_blank"
              data-mx-label={`pip-section-expanded-edit-${mxSectionLabel}`}
            >
              <FontAwesomeIcon icon={faPencil} size="2x" />
            </ExpandedSectionButton>
          </div>

          {/* Actual content of the section */}
          <div className="flex-[1 0 auto]">
            {content || <em>(empty)</em>}
          </div>

          {/* Child that makes sure the floating section does not overlow (in case the section is empty) */}
          <div className="clear-both" />
        </div>
      )}
    </div>
  );
}

function ExpandedSectionButton(props) {
  return (
    <a className="hover:bg-gray-300 p-2 rounded-xl" {...props} />
  );
}

function TitleButton({ ...props }) {
  return (
    <button
      {...props}
      className="grow-0 self-center border rounded-full w-6 h-6 md:w-8 md:h-8 bg-brand border-brand hover:bg-indigo-700 text-white transition-all font-medium outline-hidden text-sm focus:outline-hidden disabled:bg-disabled disabled:border-disabled"
    />
  );
}

export default function PipNote() {
  const { noteUuid } = useParams();
  const navigate = useNavigate();
  const pimsFormat = usePimsExportFormat();
  const [shouldUseCapitalSubtitlesCopy] = useLocalStorage(
    "should-use-capital-subtitles-copy",
  );

  const { data, loading, refetch } = useQuery(GET_NOTE, {
    variables: { uuid: noteUuid },
    fetchPolicy: "network-only",
  });

  const note = data?.note;

  useWatchUserWsEvent({
    onMessage: async () => {
      try {
        await refetch();
      } catch (e) {
        // Sure would be nice if we had error codes
        if (e.message === "Note not found.") {
          alert("error", "This note has been deleted.");
          navigate("/pip/notes");
        }
      }
    },
    eventName: "note_update",
  });

  const copiableSectionKeys = new Set(
    note?.shouldUseCustomForm
      ? ["custom"]
      : ["subjective", "objective", "assessment", "plan"],
  );

  const [copiedSectionsKeys, setCopiedSectionKeys] = useState(
    new Set(),
  );

  const isNoteExported = note?.hasBeenExported && note?.isApproved;

  const markSectionsCopied = (keys) => {
    const newCopiedSectionKeys = new Set([
      ...copiedSectionsKeys,
      ...keys,
    ]);
    setCopiedSectionKeys(newCopiedSectionKeys);

    const nonCopiedSectionsKeys = Array.from(
      copiableSectionKeys,
    ).filter((key) => !newCopiedSectionKeys.has(key));
    if (!isNoteExported && !nonCopiedSectionsKeys.length) {
      markNoteExported();
    }
  };

  const fullSoap = (() => {
    if (!note) return "";

    if (note.shouldUseCustomForm) {
      return note.customFormFields[0]?.textValue || "";
    }

    function header(str) {
      return shouldUseCapitalSubtitlesCopy
        ? str.toLocaleUpperCase()
        : str;
    }

    return [
      header("Subjective: "),
      note.subjective,
      "",
      header("Objective: "),
      note.objective,
      "",
      header("Assessment: "),
      note.assessment,
      "",
      header("Plan: "),
      note.plan,
      "",
    ].join("\n");
  })();

  const customFieldLabel = (() => {
    switch (note?.jobType) {
      case "dental_record":
        return "Dental Record";
      case "recap":
        return "Recap";
      case "callback":
        return "Callback";
      case "abdominal_ultrasound":
        return "Abd. Ultrasound";
      case "template":
        return "Adaptive Template";
      case "dictation":
        return "Transcription";
      default:
        return "Contents";
    }
  })();

  const [modifyNotes] = useMutation(MARK_NOTES_EXPORTED);
  const [modifyNote] = useMutation(MODIFY_NOTE);

  const markNoteExported = () =>
    trackOperation("pip-mark-note-exported", async (ctx) => {
      ctx.addProperties({
        noteUuid: note.uuid,
      });

      await modifyNotes({
        variables: {
          noteUuids: [note.uuid],
          action: "mark_exported",
        },
      });
      await modifyNote({
        variables: {
          noteInput: {
            noteUuid: noteUuid,
            isApproved: true,
          },
        },
      });
      await refetch();
      alert("info", `Your note was marked as exported!`);
    });

  const ownerDisplayName = useDisplayName(note?.user, {
    includeDisplayName: true,
  });

  return (
    <div>
      <Loading shouldShow={loading} />

      {note && (
        <>
          <div className="flex flex-row items-start gap-4 py-4 sticky top-0 bg-primary z-999">
            <button
              className="text-gray-700 dark:text-gray-300 hover:opacity-50"
              data-mx-label="pip-back"
              onClick={() => navigate("/pip/notes")}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                size="2x"
                color="currentColor"
              />
            </button>

            <h2 className="text-left text-gray-700 dark:text-gray-300 grow text-base">
              {note.title}
            </h2>
            <div className="self-start">
              <MaximizedPipControls />
            </div>
          </div>

          <div className="flex flex-row space-between w-full justify-between items-center">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-300">
                {note.jobTypeName || "Quicknote"}
              </div>

              <NoteCreatedAt createdAt={note.createdAt} size="sm" />
              <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon
                  icon={faUserGroup}
                  size="sm"
                  className="fill-current"
                />{" "}
                {`${ownerDisplayName}'s Note`}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="flex flex-row gap-2 justify-end">
                <Tooltip
                  title="Edit in Scribenote"
                  placement="bottom"
                >
                  <div>
                    <TitleButton
                      data-mx-label="pip-edit-title"
                      onClick={() =>
                        window.open(`/edit_note/${noteUuid}`)
                      }
                    >
                      <FontAwesomeIcon icon={faPencil} size="l" />
                    </TitleButton>
                  </div>
                </Tooltip>

                <Tooltip title="Copy entire note" placement="bottom">
                  <div>
                    <TitleButton
                      data-mx-label="pip-copy-full-title"
                      onClick={() => {
                        copyToClipboard(fullSoap, pimsFormat);
                        markSectionsCopied(copiableSectionKeys);
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} size="l" />
                    </TitleButton>
                  </div>
                </Tooltip>

                <Tooltip title="Mark as exported" placement="bottom">
                  <div>
                    <TitleButton
                      data-mx-label="pip-mark-exported-title"
                      disabled={
                        note.hasBeenExported && note.isApproved
                      }
                      onClick={markNoteExported}
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        size="l"
                      />
                    </TitleButton>
                  </div>
                </Tooltip>
              </div>
              <NoteStatus note={note} />
            </div>
          </div>

          {/* SOAP layout */}
          {!note.shouldUseCustomForm && (
            <>
              <DraggableButton
                label="Copy Subjective"
                noteUuid={note.uuid}
                mxSectionLabel="subjective"
                colorClasses="bg-blue-400 hover:bg-blue-500 disabled:bg-gray-400"
                onCopy={() => markSectionsCopied(["subjective"])}
                format={pimsFormat}
              >
                {note.subjective}
              </DraggableButton>
              <DraggableButton
                label="Copy Objective"
                noteUuid={note.uuid}
                mxSectionLabel="objective"
                colorClasses="bg-pink-400 hover:bg-pink-500 disabled:bg-gray-400"
                onCopy={() => markSectionsCopied(["objective"])}
                format={pimsFormat}
              >
                {note.objective}
              </DraggableButton>
              <DraggableButton
                label="Copy Assessment"
                noteUuid={note.uuid}
                mxSectionLabel="assessment"
                colorClasses="bg-green-400 hover:bg-green-500 disabled:bg-gray-400"
                onCopy={() => markSectionsCopied(["assessment"])}
                format={pimsFormat}
              >
                {note.assessment}
              </DraggableButton>
              <DraggableButton
                label="Copy Plan"
                noteUuid={note.uuid}
                mxSectionLabel="plan"
                colorClasses="bg-yellow-400 dark:bg-yellow-500 hover:bg-yellow-500 dark:hover:bg-yellow-600 disabled:bg-gray-400"
                onCopy={() => markSectionsCopied(["plan"])}
                format={pimsFormat}
              >
                {note.plan}
              </DraggableButton>
              <div className="border-t border-gray-200 dark:border-gray-600 my-4"></div>
              <DraggableButton
                label="Copy Full SOAP"
                noteUuid={note.uuid}
                mxSectionLabel="full-soap"
                onCopy={() => markSectionsCopied(copiableSectionKeys)}
                format={pimsFormat}
              >
                {fullSoap}
              </DraggableButton>

              <div className="border-t border-gray-200 dark:border-gray-600 my-4"></div>
              <DraggableButton
                label="Copy Client Summary"
                noteUuid={note.uuid}
                mxSectionLabel="client-summary"
                format={pimsFormat}
              >
                {note.clientSummary}
              </DraggableButton>

              <DraggableButton
                label="Copy Additional Notes"
                noteUuid={note.uuid}
                mxSectionLabel="additional-notes"
                format={pimsFormat}
              >
                {note.additionalNotes}
              </DraggableButton>
            </>
          )}

          {/* Non-SOAP layout */}
          {note.shouldUseCustomForm &&
            note.customFormFields.map((customFormField, i) => {
              return (
                <DraggableButton
                  key={i}
                  label={`Copy ${customFieldLabel}`}
                  noteUuid={note.uuid}
                  mxSectionLabel="custom"
                  onCopy={() => markSectionsCopied(["custom"])}
                  format={pimsFormat}
                >
                  {customFormField.textValue}
                </DraggableButton>
              );
            })}

          <div>
            <h5 className="text-xs p-2 my-4 text-brand bg-brand-subtle w-full text-center rounded-xl">
              <FontAwesomeIcon icon={faInfoCircle} /> Drag and drop
              the buttons, or click to copy and paste into your PIMS!
            </h5>
          </div>

          <button
            disabled={note.hasBeenExported && note.isApproved}
            onClick={markNoteExported}
            className="rounded-full flex-row text-center h-11 w-full text-white bg-brand hover:bg-indigo-600 disabled:bg-disabled my-10"
            data-mx-label="pip-mark-exported"
          >
            Mark as Exported <FontAwesomeIcon icon={faCheckCircle} />
          </button>
        </>
      )}
    </div>
  );
}
